// API Configuration from environment variables
const API_CONFIG = {
  POOL_SERVICE:
    import.meta.env.VITE_API_BASE_URL || "https://api.poolchemicaldata.online",
  MEASUREMENTS_SERVICE:
    import.meta.env.VITE_API_BASE_URL || "https://api.poolchemicaldata.online",
  AUTH_SERVICE:
    import.meta.env.VITE_AUTH_BASE_URL || "https://api.poolchemicaldata.online",
};

// Token management utilities
const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem("poolchem_token");
  } catch {
    return null;
  }
};

const removeAuthToken = (): void => {
  try {
    localStorage.removeItem("poolchem_token");
    localStorage.removeItem("poolchem_user");
  } catch {
    // Handle storage errors silently
  }
};

// JWT token validation
const isTokenValid = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

// Generic fetch wrapper with JWT authentication
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  // Get auth token
  const token = getAuthToken();
  const isFormDataBody =
    typeof FormData !== "undefined" && options.body instanceof FormData;

  // Prepare headers
  const headers: HeadersInit = {
    Accept: "application/json",
    ...(isFormDataBody ? {} : { "Content-Type": "application/json" }),
    ...((options.headers as Record<string, string>) || {}),
  };

  // Add JWT token to headers if available and valid
  if (token && isTokenValid(token)) {
    headers.Authorization = `Bearer ${token}`;
  }

  const defaultOptions: RequestInit = {
    headers,
    mode: "cors",
  };

  try {
    const response = await fetch(url, { ...defaultOptions, ...options });

    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401) {
      removeAuthToken();
      // Redirect to login page
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      throw new Error("Sesión expirada. Por favor, inicia sesión nuevamente.");
    }

    // Handle other errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        (typeof errorData?.message === "string" && errorData.message) ||
        `HTTP error ${response.status}`;

      const error = new Error(errorMessage) as Error & {
        status?: number;
        data?: unknown;
      };
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  } catch (error) {
    // Re-throw fetch errors
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Error de conexión con el servidor");
  }
}

// Authenticated fetch wrapper for API calls
async function fetchApiAuth<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getAuthToken();

  if (!token) {
    throw new Error("No se encontró token de autenticación");
  }

  if (!isTokenValid(token)) {
    removeAuthToken();
    throw new Error("Token de autenticación expirado");
  }

  return fetchApi<T>(url, options);
}

async function fetchApiNullable<T>(
  url: string,
  options: RequestInit = {},
): Promise<T | null> {
  try {
    return await fetchApi<T>(url, options);
  } catch (error) {
    if (
      error instanceof Error &&
      (error as Error & { status?: number }).status === 404
    ) {
      return null;
    }
    throw error;
  }
}

export { API_CONFIG, fetchApi, fetchApiAuth, fetchApiNullable, getAuthToken };
